/**
 * ImpersonationBanner
 * 
 * Floating banner shown when admin is impersonating a user.
 * Reads state from sessionStorage and provides exit button.
 */

import React, { useState, useEffect } from 'react';
import { clearLastRestaurantId } from '../../services/storage/lastRestaurant';
import { removeLocalAuthSessionOnly } from './impersonationSession';

const IMPERSONATION_KEY = 'deskbay_impersonation';
// Cross-tab exit signal. sessionStorage is per-tab, so a second impersonation tab
// on the same user would otherwise keep showing the banner after another tab exits.
// Writing this localStorage key fires a `storage` event in the OTHER tabs, which
// then clear their own marker and leave — no lingering "second impersonation".
const IMPERSONATION_EXIT_KEY = 'deskbay_impersonation_exit';

function clearImpersonationAndLeave() {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    window.close();
    // Fallback if window.close() doesn't work (not opened by script, mobile, PWA).
    setTimeout(() => {
        window.location.href = '/login';
    }, 300);
}

export function ImpersonationBanner() {
    const [impersonation, setImpersonation] = useState<{
        targetUserId: string;
        targetEmail: string;
        isActive: boolean;
    } | null>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        try {
            const stored = sessionStorage.getItem(IMPERSONATION_KEY);
            if (stored) {
                const data = JSON.parse(stored);
                if (data.isActive) {
                    setImpersonation(data);
                }
            }
        } catch {
            // ignore parse errors
        }
    }, []);

    // Listen for an exit signal broadcast by another tab. Only act if THIS tab is
    // itself impersonating — a normal user tab must never be redirected by it.
    useEffect(() => {
        const onExitSignal = (e: StorageEvent) => {
            if (e.key !== IMPERSONATION_EXIT_KEY || !e.newValue) return;
            if (!sessionStorage.getItem(IMPERSONATION_KEY)) return; // not impersonating here
            setImpersonation(null);
            clearImpersonationAndLeave();
        };
        window.addEventListener('storage', onExitSignal);
        return () => window.removeEventListener('storage', onExitSignal);
    }, []);

    const handleExit = async () => {
        // Clear the remembered restaurant so a later real login on this device does
        // NOT inherit the impersonated user's restaurant context (cross-account bleed).
        // La preferenza è ora intestata all'utente (qui: il target impersonificato),
        // quindi va rimossa con la sua chiave e non con quella globale.
        if (impersonation?.targetUserId) {
            await clearLastRestaurantId(impersonation.targetUserId).catch(() => { /* best effort */ });
        }

        // Notify any other impersonation tab (same origin) to exit as well.
        try {
            localStorage.setItem(IMPERSONATION_EXIT_KEY, String(Date.now()));
            localStorage.removeItem(IMPERSONATION_EXIT_KEY);
        } catch {
            // ignore — cross-tab sync is best-effort
        }

        // Remove ONLY the local session copy — deliberately NOT signOut():
        // the server-side session is SHARED with the admin panel (same tokens),
        // and revoking it would log the panel out at its next token refresh
        // (up to ~1h later). See impersonationSession.ts for details.
        await removeLocalAuthSessionOnly();
        clearImpersonationAndLeave();
    };

    if (!impersonation) return null;

    const isNativeIOS = typeof window !== 'undefined' &&
        'Capacitor' in window &&
        window.Capacitor?.getPlatform() === 'ios';

    const shouldShowExpanded = isExpanded || isHovered;

    const eyeIcon = (cls: string) => (
        <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
    );

    return (
        <>
            {/* Desktop / tablet (≥ md): top collapsible banner, expands on hover */}
            <div
                className={`hidden md:block fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg transition-all duration-300 ease-in-out ${isNativeIOS && shouldShowExpanded ? 'pt-[calc(var(--safe-area-top)+0.5rem)]' : ''}`}
                style={{
                    minHeight: shouldShowExpanded ? (isNativeIOS ? 'calc(var(--safe-area-top) + 3rem)' : '40px') : '8px',
                    transform: shouldShowExpanded ? 'translateY(0)' : 'translateY(-32px)'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className="px-4 py-2 flex items-center justify-between">
                    <div className={`flex items-center gap-3 text-sm font-bold transition-all duration-300 ${shouldShowExpanded ? 'opacity-100' : 'opacity-0'}`}>
                        {eyeIcon('w-4 h-4 flex-shrink-0')}
                        <span>Modalità Admin &mdash; Stai visualizzando come:</span>
                        <span className="font-black">{impersonation.targetEmail}</span>
                    </div>

                    <div className="flex items-center gap-2">
                        {shouldShowExpanded && (
                            <button
                                onClick={handleExit}
                                className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Esci
                            </button>
                        )}

                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1 hover:bg-white/20 rounded transition-all duration-200"
                            aria-label={isExpanded ? "Comprimi banner" : "Espandi banner"}
                        >
                            <svg
                                className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile (< md): floating pill bottom-right, in the thumb zone.
                Collapsed = 48px round badge; tap → expands to a card with email + Esci.
                Positioned above the bottom nav and the iOS home bar (safe-area). */}
            <div className="md:hidden">
                {isExpanded && (
                    // Invisible backdrop: a tap outside the card closes it
                    <div
                        className="fixed inset-0 z-[9998]"
                        onClick={() => setIsExpanded(false)}
                        aria-hidden="true"
                    />
                )}
                <div
                    className="fixed right-4 z-[9999]"
                    style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)' }}
                >
                    {isExpanded ? (
                        <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-2xl shadow-xl shadow-orange-500/30 p-3 max-w-[calc(100vw-2rem)] animate-fadeIn">
                            <div className="flex items-center gap-2 text-xs font-bold mb-2.5 pr-1">
                                {eyeIcon('w-4 h-4 flex-shrink-0')}
                                <span className="truncate max-w-[210px]">Admin: <span className="font-black">{impersonation.targetEmail}</span></span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleExit}
                                    className="flex-1 px-4 py-2.5 bg-white/25 active:bg-white/40 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Esci
                                </button>
                                <button
                                    onClick={() => setIsExpanded(false)}
                                    className="p-2.5 active:bg-white/20 rounded-xl"
                                    aria-label="Comprimi"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setIsExpanded(true)}
                            className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg shadow-orange-500/40 flex items-center justify-center opacity-90 active:scale-95 transition-transform"
                            aria-label="Modalità admin attiva — apri controlli"
                        >
                            {eyeIcon('w-5 h-5')}
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
